import { dirname } from 'node:path';
import { fileURLToPath } from 'node:url';
import type { EmbeddingProvider } from './types.js';
import type { InferenceSession } from 'onnxruntime-node';
import { MODEL_FILENAME, ensureModel, findModel } from './model-cache.js';

// onnxruntime-node is a peer/optional dependency — lazy import to allow graceful failure
let ort: { InferenceSession: typeof import('onnxruntime-node').InferenceSession; Tensor: typeof import('onnxruntime-node').Tensor } | null = null;

async function loadOrt(): Promise<NonNullable<typeof ort>> {
  if (ort !== null) return ort;
  const mod = await import('onnxruntime-node');
  ort = { InferenceSession: mod.InferenceSession, Tensor: mod.Tensor };
  return ort;
}

const THIS_DIR = dirname(fileURLToPath(import.meta.url));

function resolveModelPath(): string | null {
  return findModel(THIS_DIR);
}

/**
 * DEGRADED: Hash-based tokenizer — NOT real BERT tokenization.
 * Maps whitespace-split tokens to pseudo vocabulary IDs via a deterministic hash.
 * This produces consistent but semantically poor embeddings compared to proper
 * WordPiece tokenization. Similarity scores will be unreliable.
 *
 * For production use, strongly prefer Ollama with nomic-embed-text or OpenAI embeddings.
 * ONNX is kept only as an offline fallback when no network provider is available.
 */
function simpleTokenize(
  text: string,
  maxLength: number,
): { inputIds: BigInt64Array; attentionMask: BigInt64Array; tokenTypeIds: BigInt64Array } {
  const VOCAB_SIZE = 30522; // bge-small vocab size
  const CLS_ID = 101n;
  const SEP_ID = 102n;
  const PAD_ID = 0n;

  const words = text.toLowerCase().trim().split(/\s+/).filter(w => w.length > 0);

  // Hash each word to a pseudo-ID in [1, VOCAB_SIZE)
  const wordIds: bigint[] = words.slice(0, maxLength - 2).map(word => {
    let hash = 0;
    for (let i = 0; i < word.length; i++) {
      hash = ((hash << 5) - hash + word.charCodeAt(i)) | 0;
    }
    // Map to positive range within vocab
    const id = ((hash % (VOCAB_SIZE - 2)) + (VOCAB_SIZE - 2)) % (VOCAB_SIZE - 2) + 1;
    return BigInt(id);
  });

  const tokenCount = wordIds.length + 2; // +2 for CLS and SEP
  const inputIds = new BigInt64Array(maxLength);
  const attentionMask = new BigInt64Array(maxLength);
  const tokenTypeIds = new BigInt64Array(maxLength);

  // [CLS] ...tokens... [SEP] [PAD]...
  inputIds[0] = CLS_ID;
  attentionMask[0] = 1n;
  for (let i = 0; i < wordIds.length; i++) {
    inputIds[i + 1] = wordIds[i];
    attentionMask[i + 1] = 1n;
  }
  inputIds[wordIds.length + 1] = SEP_ID;
  attentionMask[wordIds.length + 1] = 1n;

  // Remaining positions are already 0 (PAD)
  for (let i = tokenCount; i < maxLength; i++) {
    inputIds[i] = PAD_ID;
    attentionMask[i] = 0n;
    tokenTypeIds[i] = 0n;
  }

  return { inputIds, attentionMask, tokenTypeIds };
}

/**
 * Mean-pool token embeddings, respecting the attention mask.
 */
function meanPool(lastHiddenState: Float32Array, attentionMask: BigInt64Array, seqLen: number, hiddenSize: number): Float32Array {
  const result = new Float32Array(hiddenSize);
  let tokenCount = 0;

  for (let t = 0; t < seqLen; t++) {
    if (attentionMask[t] === 0n) continue;
    tokenCount++;
    const offset = t * hiddenSize;
    for (let h = 0; h < hiddenSize; h++) {
      result[h] += lastHiddenState[offset + h];
    }
  }

  if (tokenCount > 0) {
    for (let h = 0; h < hiddenSize; h++) {
      result[h] /= tokenCount;
    }
  }

  return result;
}

/**
 * L2-normalize a vector in place, returning it.
 */
function l2Normalize(vec: Float32Array): Float32Array {
  let sumSq = 0;
  for (let i = 0; i < vec.length; i++) {
    sumSq += vec[i] * vec[i];
  }
  const norm = Math.sqrt(sumSq);
  if (norm > 0) {
    for (let i = 0; i < vec.length; i++) {
      vec[i] /= norm;
    }
  }
  return vec;
}

export class OnnxEmbeddingProvider implements EmbeddingProvider {
  readonly name = 'bge-small-en-v1.5';
  readonly dimensions = 384;
  readonly maxTokens = 512;

  private session: InferenceSession | null = null;
  private modelPath: string | null;
  /** An explicit constructor path is never replaced by the lazy download. */
  private readonly pinnedPath: boolean;

  constructor(modelPath?: string) {
    this.pinnedPath = modelPath !== undefined;
    this.modelPath = modelPath ?? resolveModelPath();
  }

  async isAvailable(): Promise<boolean> {
    try {
      // v0.29.3: the model is fetched lazily on first use when the install
      // script never ran (npm --ignore-scripts, hardened .npmrc, read-only
      // global package dir). Without this, a perfectly fine install reports
      // "no embedding route at all".
      if (this.modelPath === null && !this.pinnedPath) {
        this.modelPath = await ensureModel(THIS_DIR);
      }
      if (this.modelPath === null) return false;
      await this.ensureSession();
      return true;
    } catch {
      return false;
    }
  }

  async embed(text: string): Promise<Float32Array> {
    const session = await this.ensureSession();
    const { inputIds, attentionMask, tokenTypeIds } = simpleTokenize(text, this.maxTokens);

    const { Tensor } = await loadOrt();
    const feeds = {
      input_ids: new Tensor('int64', inputIds, [1, this.maxTokens]),
      attention_mask: new Tensor('int64', attentionMask, [1, this.maxTokens]),
      token_type_ids: new Tensor('int64', tokenTypeIds, [1, this.maxTokens]),
    };

    const output = await session.run(feeds);

    // The model output key is typically 'last_hidden_state' or the first output
    const outputTensor = output['last_hidden_state'] ?? Object.values(output)[0];
    if (outputTensor === undefined) {
      throw new Error('ONNX model returned no output tensors');
    }

    const hiddenState = outputTensor.data as Float32Array;
    const pooled = meanPool(hiddenState, attentionMask, this.maxTokens, this.dimensions);
    return l2Normalize(pooled);
  }

  async embedBatch(texts: string[]): Promise<Float32Array[]> {
    const results: Float32Array[] = [];
    for (const text of texts) {
      results.push(await this.embed(text));
    }
    return results;
  }

  private async ensureSession(): Promise<InferenceSession> {
    if (this.session !== null) return this.session;

    if (this.modelPath === null) {
      throw new Error(
        `ONNX model not found. Run: npx neuromcp-download-model\n` +
        `Expected location: ~/.neuromcp/models/${MODEL_FILENAME} (or <package>/models/)`,
      );
    }

    const { InferenceSession: IS } = await loadOrt();
    this.session = await IS.create(this.modelPath, {
      executionProviders: ['cpu'],
    });
    return this.session;
  }
}
